// Pure DTO + VMs + functions — no Angular, no DOM, testable in isolation.
// Shape preserved exactly from GET /liste/lists/:id/dashboard-stats.

// ---- DTO ----------------------------------------------------------------

export interface RawBucket {
  y: number; m: number; d: number; h: number; mn: number;
  in: number; out: number;
}

export interface RecentEventRaw {
  date: string; type: string; firstName: string; lastName: string;
  canal: string; idTplTicket: string;
}

export interface Totals { in: number; out: number; }

export interface DashboardStats {
  ticketsByTemplate: Record<string, number>;
  scansByTemplate: Record<string, { scansIn: number; scansOut: number }>;
  scansByAccess: Record<string, { scansIn: number; scansOut: number }>;
  timeSeries: RawBucket[];
  recentEvents: RecentEventRaw[];
  totals: Totals;
}

// ---- View Models --------------------------------------------------------

export type Granularity = 'M' | 'H' | 'J';

export const MAX_M = 2880;
export const MAX_H = 2400;
export const MAX_D = 1825;

/** Série du donut pour le type de graphe sélectionné (iso legacy : mêmes valeurs pour les 2 charts). */
export interface DonutVm { d1: number; d2: number; }

/** Row for both access-list and tarif-list (DashboardScanListComponent). */
export interface ScanRowVm {
  id: string; name: string; propScan: number; totalS: number; toScan: number;
}

export interface RecentControlVm {
  date: string; type: string; nomEtPrenom: string; tplName: string; isIn: boolean;
}

export interface ConfigStepVm { done: boolean; }

export interface TimelineDisplay {
  titb2: string; mper: string; mtntot: string; texttotparT: string;
  /** Série du donut (d1 = valeur, d2 = complément) pour le type sélectionné. */
  d1: number; d2: number;
}

export const SYNTHETIC_ACCESS_IDS = ['fromBo', 'delAccess'] as const;

// ---- Internal date helpers ----------------------------------------------

function p2(n: number): string { return n < 10 ? '0' + n : '' + n; }

function tms(b: RawBucket): number {
  return new Date(b.y, b.m - 1, b.d, b.h || 0, b.mn || 0).getTime();
}

export function fmtM(b: { d: number; m: number; y: number; h: number; mn: number }): string {
  return p2(b.d) + '/' + p2(b.m) + ' à ' + p2(b.h) + 'h' + p2(b.mn);
}

export function fmtH(b: { d: number; m: number; y: number; h: number; mn: number }): string {
  return p2(b.d) + '/' + p2(b.m) + ' à ' + p2(b.h) + 'h';
}

export function fmtD(b: { d: number; m: number; y: number; h: number; mn: number }): string {
  return p2(b.d) + '/' + p2(b.m);
}

// ---- Pure functions -----------------------------------------------------

/**
 * Fills time gaps between two consecutive buckets.
 * Returns formatted date labels for missing intervals (gap points carry count 0 upstream).
 */
export function fillGaps(
  prev: RawBucket,
  next: RawBucket,
  step: number,
  fmtFn: (b: { d: number; m: number; y: number; h: number; mn: number }) => string,
  maxEntries: number,
  currentCount: number
): string[] {
  const gaps: string[] = [];
  let d1 = tms(prev) + step;
  const d2 = tms(next);
  while (d1 < d2 && (currentCount + gaps.length) < maxEntries) {
    const dd = new Date(d1);
    gaps.push(fmtFn({
      d: dd.getDate(), m: dd.getMonth() + 1, y: dd.getFullYear(),
      h: dd.getHours(), mn: dd.getMinutes(),
    }));
    d1 += step;
  }
  return gaps;
}

/** Rolls minute-level buckets up to hour ('H') or day ('J'). Passthrough for 'M'. */
export function rollUpBuckets(minuteBuckets: RawBucket[], granularity: Granularity): RawBucket[] {
  if (granularity === 'M') return minuteBuckets;
  const map: Record<string, RawBucket> = {};
  for (const b of minuteBuckets) {
    const key = granularity === 'H'
      ? `${b.y}-${p2(b.m)}-${p2(b.d)}-${p2(b.h)}`
      : `${b.y}-${p2(b.m)}-${p2(b.d)}`;
    if (!map[key]) {
      map[key] = granularity === 'H'
        ? { y: b.y, m: b.m, d: b.d, h: b.h, mn: 0, in: 0, out: 0 }
        : { y: b.y, m: b.m, d: b.d, h: 0, mn: 0, in: 0, out: 0 };
    }
    map[key].in += b.in;
    map[key].out += b.out;
  }
  return Object.keys(map).sort().map(k => map[k]);
}

/**
 * Computes overmax priority for one series at 3 granularities.
 * Returns 0 (none), 1 (M saturated), 2 (H saturated), 3 (D saturated).
 * Seuils exacts reproduits de .ts:444-462 — ne pas modifier.
 */
export function computeOvermax(
  entriesM: number, entriesH: number, entriesD: number,
  maxM: number, maxH: number, maxD: number
): number {
  if (entriesD >= maxD) return 3;
  if (entriesH >= maxH) return 2;
  if (entriesM >= maxM) return 1;
  return 0;
}

/**
 * Empirical zoom coefficient for the timeline slider.
 * Coefficients 0.77-0.92 sont des magic numbers du jQuery original — ne pas altérer.
 * mt = position du slider (8-62).
 */
export function regulZoom(omax: number, mt: number): number {
  let xr = 1;
  if (omax === 2) {
    if (mt < 15)       xr = 0.800;
    else if (mt < 20)  xr = 0.780;
    else if (mt < 25)  xr = 0.780;
    else if (mt >= 30) xr = 0.860;
  } else if (omax === 1) {
    if (mt < 10)       xr = 0.780;
    else if (mt < 15)  xr = 0.780;
    else if (mt < 20)  xr = 0.795;
    else if (mt < 25)  xr = 0.795;
    else if (mt < 30)  xr = 0.800;
    else if (mt < 45)  xr = 0.810;
    else               xr = 0.810;
  } else if (omax === 0) {
    if (mt < 12)       xr = 0.770;
    else if (mt < 15)  xr = 0.780;
    else if (mt < 18)  xr = 0.795;
    else if (mt < 20)  xr = 0.800;
    else if (mt < 22)  xr = 0.810;
    else if (mt < 25)  xr = 0.805;
    else if (mt < 28)  xr = 0.800;
    else if (mt < 29)  xr = 0.830;
    else if (mt < 35)  xr = 0.832;
    else if (mt < 38)  xr = 0.860;
    else if (mt < 42)  xr = 0.876;
    else if (mt < 48)  xr = 0.893;
    else if (mt < 52)  xr = 0.902;
    else if (mt < 57)  xr = 0.911;
    else if (mt < 59)  xr = 0.917;
    else               xr = 0.920;
  }
  return xr;
}

/**
 * Guards clamp + division-by-zero for gauge bars.
 * Returns integer percentage 0-100. Never NaN.
 */
export function clampGauge(value: number, total: number): number {
  if (total === 0) return 0;
  const clamped = value > total ? total : value;
  return Math.round((clamped / total) * 100);
}

/**
 * 3 métier branches: stock / guichet / Invited + nominal.
 * Libellés métier en dur — ne pas paramétrer (.ts:307-319).
 */
export function computeFirstAndLastName(firstName: string, lastName: string, canal: string): string {
  if (canal === 'stock' && firstName.slice(0, 7) === 'prenom-' && lastName.slice(0, 4) === 'nom-') {
    return 'Impression de stock';
  }
  if (canal === 'guichet' && firstName === 'impression' && lastName.slice(0, 9) === 'sur place') {
    return 'Vente sur place';
  }
  if (canal === 'Invited' && firstName === 'gratuite' && lastName === 'Invitation') {
    return 'Invitation';
  }
  const fn = firstName.trim();
  const ln = lastName.trim();
  return ln.slice(0, 1).toUpperCase() + ln.slice(1).toLowerCase()
       + ' ' + fn.slice(0, 1).toUpperCase() + fn.slice(1).toLowerCase();
}

/** Stable sort by numeric property. Returns new array. */
export function sortPropScan<T>(list: T[], key: keyof T, dir: 'asc' | 'desc' = 'asc'): T[] {
  return [...list].sort((a, b) => {
    const va = a[key] as number; // any-justifié: clé générique sur T, valeur connue numérique à l'appel
    const vb = b[key] as number; // any-justifié: clé générique sur T, valeur connue numérique à l'appel
    return dir === 'asc' ? va - vb : vb - va;
  });
}

// ---- Mappers ------------------------------------------------------------

export function fmt(n: number): string { return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '); }

export function toRecentControls(
  events: RecentEventRaw[],
  tplNameMap: Record<string, string>
): RecentControlVm[] {
  return events.map(ev => ({
    date: ev.date || '',
    type: ev.type,
    nomEtPrenom: computeFirstAndLastName(ev.firstName || '', ev.lastName || '', ev.canal || ''),
    tplName: tplNameMap[ev.idTplTicket] || 'Inconnu',
    isIn: ev.type === 'in',
  }));
}

export interface TplScanEntry {
  id: string; name: string; scanNbr?: number; infinity?: boolean | string;
  toScan?: number; totalIn?: number; totalOut?: number; totalS?: number;
  [k: string]: any; // any-justifié: blob dataList.tpl passé tel quel depuis l'API, propriétés additionnelles non typées
}

/** Enrichit les entrées tpl de la liste avec les stats serveur. Retourne totaux dérivés. */
export function computeTplStats(
  tplRaw: TplScanEntry[],
  ticketsByTemplate: Record<string, number>,
  scansByTemplate: Record<string, { scansIn: number; scansOut: number }>
): { tplScan: TplScanEntry[]; toTick: number; toScan: number } {
  let toTick = 0;
  let toScan = 0;
  const tplScan = tplRaw.map(t => {
    const tplId = t.id;
    const tplTicketCount = ticketsByTemplate[tplId] || 0;
    toTick += tplTicketCount;
    const infinity = t.infinity === true || t.infinity === 'true';
    const scanLimit = infinity ? tplTicketCount : tplTicketCount * (t.scanNbr || 1);
    toScan += scanLimit;
    const tplScans = scansByTemplate[tplId] || { scansIn: 0, scansOut: 0 };
    const totalS = tplScans.scansIn - tplScans.scansOut;
    return {
      ...t,
      toScan: scanLimit,
      totalIn: tplScans.scansIn,
      totalOut: tplScans.scansOut,
      totalS,
    };
  });
  return { tplScan, toTick, toScan };
}

export function toTemplateGauges(
  tplScan: TplScanEntry[]
): ScanRowVm[] {
  return sortPropScan(
    tplScan.map(t => ({
      id: t.id,
      name: t.name,
      totalS: Math.min(t.totalS ?? 0, t.toScan ?? 0),
      toScan: t.toScan ?? 0,
      propScan: clampGauge(t.totalS ?? 0, t.toScan ?? 0),
    })),
    'propScan',
    'desc'
  );
}

export interface DkEntry {
  _id: string; name: string;
  enableList?: string[]; // absente = toutes les listes, comme l'API (allowedLists 'all') et l'app de scan
  totalIn?: number; totalOut?: number; totalS?: number;
  [k: string]: any; // any-justifié: blob getAllKeys API, propriétés additionnelles non typées
}

/** Construit les jauges d'accès depuis dk (getAllKeys) + stats serveur. */
export function computeAccessGauges(
  rawDk: DkEntry[],
  scansByAccess: Record<string, { scansIn: number; scansOut: number }>,
  idActif: string,
  totS: number
): ScanRowVm[] {
  // Clone + ajoute Backoffice et Accès supprimés (catch-all) comme dans le jQuery original
  const dk: DkEntry[] = [
    ...rawDk.map(d => ({ ...d, totalIn: 0, totalOut: 0, totalS: 0 })),
    { _id: SYNTHETIC_ACCESS_IDS[0], name: 'Backoffice', enableList: [], totalIn: 0, totalOut: 0, totalS: 0 },
    { _id: SYNTHETIC_ACCESS_IDS[1], name: 'Accès supprimés', enableList: [], totalIn: 0, totalOut: 0, totalS: 0 },
  ];

  for (const staffId of Object.keys(scansByAccess)) {
    const accessStats = scansByAccess[staffId];
    const idx = dk.findIndex(d => d._id === staffId);
    const target = idx >= 0 ? dk[idx] : dk[dk.length - 1];
    (target.totalIn as number) += accessStats.scansIn;
    (target.totalOut as number) += accessStats.scansOut;
    (target.totalS as number) += accessStats.scansIn - accessStats.scansOut;
  }

  const rows: ScanRowVm[] = [];
  for (const d of dk) {
    const inList = d.enableList?.includes(idActif) ?? true;
    const hasActivity = (d.totalIn as number) > 0;
    if (!inList && !hasActivity) continue;
    rows.push({
      id: d._id,
      name: d.name,
      totalS: (d.totalS as number) > totS ? totS : (d.totalS as number),
      toScan: totS,
      propScan: clampGauge(d.totalS as number, totS),
    });
  }

  return sortPropScan(rows, 'propScan', 'desc');
}
